import { useEffect, useState } from "react";
import {
    Box, Button, Checkbox, CircularProgress, Divider,
    FormControlLabel, Link, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useT, useTRich } from "src/i18n";
import { patchMeAsiakas } from "src/utils/quote/quoteApi";
import * as hallintaApi from "src/utils/hallinta/hallintaApi";
import { uploadAsiakasLogo } from "src/utils/quote/quoteAttachments";
import { captureError } from "src/utils/errorReporting";
import { useFieldSetter } from "src/utils/useFieldSetter";
import GlassDialog from "src/components/common/GlassDialog";

const Y_TUNNUS_REGEX = /^\d{7}-\d$/;

// Byte-identical to normaliseOfferEmail's regex in puminet5api
// routes/jerryProviderSettingsRoutes.js, on purpose: a pre-flight check so a typo
// names the field instead of coming back as a whole-dialog banner. Named because
// this repo already carries THREE other, looser anonymous email regexes
// (entitySchema.js, auth/EmailStep.jsx, PersonEditDialog.jsx) — the difference
// from those is deliberate, not drift. Never make it stricter than the server
// gate: a client that rejects what the server accepts blocks a valid address.
const OFFER_EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// Single company editor for a company asiakas (typeId=1) the caller owns under
// the BetoniJerry umbrella. Edits TWO backend entities behind one Tallenna:
//   - Perustiedot / Laskutus → the asiakas row (PATCH /api/me/asiakases/:id)
//   - Esittely / Jerry-asetukset → pumppuProviderSettings (PUT /jerry-provider-settings):
//       companyDescription (shown to customers on /valut offer cards),
//       jerryPersonId (contact person for Jerry offers). maintainsOrderInfo is
//       hidden/unused since 2026-07-18 (open-details model; pinned false at
//       offer-submit time in OfferForm — no longer editable here).
// This replaces the former standalone /hallinta "Esittely" tab so company data
// lives in one place. openingHours stays managed on betoni.online only.
//
// Perustiedot/Laskutus seed synchronously from the asiakas prop; the
// Esittely/Jerry section is fetched on open and degrades gracefully — if that
// fetch fails, basics stay fully editable/savable and only the Jerry write is
// skipped. Private rows (typeId=2) must use PersonEditDialog instead.
//
// The parent mounts this conditionally (only when open) so the component
// remounts on each open, useState re-seeds, and the load effect refetches.
//
// Props:
//   open, onClose
//   asiakas  — the asiakas row from the asiakases list (full basics prefill)
//   onSaved  — called with the asiakas PATCH response so the parent can refresh
//              the header card's name/ytunnus/address.
export default function AsiakasEditDialog({ open, onClose, asiakas, onSaved }) {
    const { token, user } = useAuth();
    const t = useT();
    const tRich = useTRich();

    // --- Perustiedot / Laskutus (asiakas) ---
    const [form, setForm] = useState(() => buildInitial(asiakas));
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | submitting | error
    const [submitError, setSubmitError] = useState(null);
    const update = useFieldSetter(setForm, errors, setErrors);

    // --- Esittely / Jerry-asetukset (pumppuProviderSettings) ---
    const [text, setText] = useState("");
    const [jerryPersonId, setJerryPersonId] = useState(null);
    const [jerryPersonName, setJerryPersonName] = useState(null);
    const [jerryPersonEmail, setJerryPersonEmail] = useState(null);
    // Delivery address for tarjouspyynnöt, independent of the login identity
    // above (fb#532). Empty string = not configured → the contact person's own
    // address is used, as before.
    const [offerEmail, setOfferEmail] = useState("");
    const [offerEmailError, setOfferEmailError] = useState(null);
    const [website, setWebsite] = useState("");
    const [publicSlug, setPublicSlug] = useState("");
    const [publicConsent, setPublicConsent] = useState(false);
    const [websiteError, setWebsiteError] = useState(null);
    const [logoBusy, setLogoBusy] = useState(false);
    const [logoError, setLogoError] = useState(null);
    const [logoDone, setLogoDone] = useState(false);
    const [persons, setPersons] = useState(null); // null = not loaded yet
    const [personsError, setPersonsError] = useState(false);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [settingsError, setSettingsError] = useState(false);

    const asiakasId = asiakas?.asiakasId;

    // Load the provider settings (description / contact / flag) on open. Kept
    // independent of the persons fetch so a persons-list failure only downgrades
    // the contact picker to a read-only field instead of blanking the section.
    useEffect(() => {
        if (!asiakasId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await hallintaApi.getJerryProviderSettings({ token, asiakasId });
                if (cancelled) return;
                setText(res?.companyDescription || "");
                setJerryPersonId(res?.jerryPersonId || null);
                setJerryPersonName(res?.jerryPersonName || null);
                setJerryPersonEmail(res?.jerryPersonEmail || null);
                setOfferEmail(res?.offerNotificationEmail || "");
                setWebsite(res?.website || "");
                setPublicSlug(res?.publicSlug || "");
                setPublicConsent(!!res?.publicListingConsentAt);
            } catch (err) {
                if (cancelled) return;
                captureError(err, { tags: { entity: "pumppuProviderSettings", operation: "get" } });
                setSettingsError(true);
            } finally {
                if (!cancelled) setSettingsLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [token, asiakasId]);

    useEffect(() => {
        if (!asiakasId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await hallintaApi.listPersons({ token, asiakasId });
                if (!cancelled) setPersons(res?.personList || []);
            } catch (err) {
                if (cancelled) return;
                captureError(err, { tags: { entity: "person", operation: "list" } });
                setPersonsError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [token, asiakasId]);

    const jerrySavable = settingsLoaded && !settingsError;
    const jerryDisabled = !settingsLoaded || settingsError;
    const selectReady = !personsError && persons !== null;
    // Keep a removed/foreign current person selectable so MUI's Select doesn't
    // warn about an out-of-range value and the stored id survives a save.
    const currentMissing = selectReady && jerryPersonId != null
        && !persons.some((p) => p.personId === jerryPersonId);

    // Surface the email new tarjouspyynnöt will actually go to, so a wrong or
    // missing address is caught here instead of failing silently (the backend
    // falls back to laskutusEmail → admins when the contact has no email).
    const selectedPerson = selectReady && jerryPersonId != null
        ? persons.find((p) => p.personId === jerryPersonId)
        : null;
    const selectedEmail = selectedPerson ? (selectedPerson.personEmail || null) : jerryPersonEmail;
    // A configured offer address WINS in jerryRecipients, so once it is set the
    // person's own address is no longer where mail lands — saying otherwise here
    // would be a confident lie about the one fact this helper exists to report.
    const offerEmailOverrides = offerEmail.trim().length > 0;
    const emailMissing = !offerEmailOverrides && jerryPersonId != null && selectedPerson && !selectedEmail;
    // Ordered so each branch is self-contained and the ladder stays flat: both
    // "no contact selected" and "nothing to say" fall through to the terminal " ".
    // The `jerryPersonId != null` guard on the address branch is LOAD-BEARING —
    // this dialog's Select onChange sets only jerryPersonId, never
    // jerryPersonEmail, so clearing the contact to "Ei valittu" leaves a stale
    // email in state that would otherwise be named as the recipient.
    const emailHelper =
        offerEmailOverrides ? t("auth.company.offersGoToOverride")
            : emailMissing ? t("auth.company.noEmailWarning")
                : jerryPersonId != null && selectedEmail
                    ? t("auth.company.offersWillGoTo", { email: selectedEmail })
                    : " ";

    function validate(f) {
        const errs = {};
        if (!f.asiakasNimi.trim()) errs.asiakasNimi = t("auth.shared.companyNameRequired");
        else if (f.asiakasNimi.length > 250) errs.asiakasNimi = t("auth.company.nameTooLong");
        if (f.ytunnus.trim() && !Y_TUNNUS_REGEX.test(f.ytunnus.trim())) {
            errs.ytunnus = t("auth.company.ytunnusFormat");
        }
        return errs;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!asiakasId) return;
        // Cleared BEFORE the validation returns below, so a stale banner from an
        // earlier failed save cannot sit above a fresh field-level error.
        setSubmitError(null);
        const v = validate(form);
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        // Mirrors normaliseOfferEmail in jerryProviderSettingsRoutes. Checked here
        // too so a typo is caught before the save instead of coming back as a
        // whole-dialog submitError that loses which field was wrong.
        const offer = offerEmail.trim();
        if (offer && !OFFER_EMAIL_RE.test(offer)) {
            setOfferEmailError(t("auth.company.offerEmailInvalid"));
            return;
        }

        setStatus("submitting");
        try {
            const writes = [
                patchMeAsiakas({
                    token,
                    id: asiakasId,
                    fields: {
                        asiakasNimi: form.asiakasNimi.trim(),
                        ytunnus: form.ytunnus.trim() || null,
                        laskutusOsoite: form.laskutusOsoite.trim() || null,
                        laskutusPostinumero: form.laskutusPostinumero.trim() || null,
                        laskutusKaupunki: form.laskutusKaupunki.trim() || null,
                    },
                }),
            ];
            // Only write the Jerry entity when its data actually loaded — never
            // clobber it with the empty defaults of a failed/mid-flight fetch.
            if (jerrySavable) {
                writes.push(hallintaApi.saveJerryProviderSettings({
                    token,
                    asiakasId,
                    fields: {
                        companyDescription: text,
                        jerryPersonId,
                        offerNotificationEmail: offer || null,
                        website: website.trim() || null,
                        publicSlug: publicSlug.trim() || null,
                        publicListingConsent: publicConsent,
                    },
                }));
            }
            const [asiakasResult] = await Promise.all(writes);
            onSaved?.(asiakasResult);
            onClose();
        } catch (err) {
            if (err?.status === 409 && err?.body?.conflict === "ytunnus") {
                setErrors((prev) => ({ ...prev, ytunnus: t("auth.company.ytunnusConflict") }));
                setStatus("error");
                return;
            }
            if (err?.status === 400 && /kotisivun/i.test(err?.body?.error || "")) {
                setWebsiteError("Anna osoite muodossa https://yritys.fi");
                setStatus("error");
                return;
            }
            captureError(err, { tags: { entity: "company-edit", operation: "save" } });
            setSubmitError(err?.message || t("auth.shared.saveFailed"));
            setStatus("error");
        }
    }

    return (
        <GlassDialog open={open} onClose={onClose} busy={status === "submitting"} title={t("auth.company.editTitle")} maxWidth="sm">
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={1.75}>
                    <TextField
                        label={t("auth.shared.companyNameLabel")}
                        fullWidth
                        value={form.asiakasNimi}
                        onChange={update("asiakasNimi")}
                        error={!!errors.asiakasNimi}
                        helperText={errors.asiakasNimi || " "}
                        autoComplete="organization"
                    />
                    <TextField
                        label={t("auth.shared.ytunnusLabel")}
                        placeholder="1234567-8"
                        fullWidth
                        value={form.ytunnus}
                        onChange={update("ytunnus")}
                        error={!!errors.ytunnus}
                        helperText={errors.ytunnus || " "}
                    />

                    <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", pt: 0.5, fontSize: 12 }}
                    >
                        {t("auth.company.billingAddressLabel")}
                    </Typography>
                    <TextField
                        label={t("auth.shared.streetAddressLabel")}
                        fullWidth
                        value={form.laskutusOsoite}
                        onChange={update("laskutusOsoite")}
                        autoComplete="street-address"
                        helperText=" "
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <TextField
                            label={t("auth.shared.postalCodeLabel")}
                            value={form.laskutusPostinumero}
                            onChange={update("laskutusPostinumero")}
                            autoComplete="postal-code"
                            sx={{ flex: { xs: "1 1 auto", sm: "0 0 40%" } }}
                            helperText=" "
                        />
                        <TextField
                            label={t("auth.shared.cityLabel")}
                            fullWidth
                            value={form.laskutusKaupunki}
                            onChange={update("laskutusKaupunki")}
                            autoComplete="address-level2"
                            helperText=" "
                        />
                    </Stack>

                    <Divider sx={{ my: 0.5 }} />

                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("auth.company.introSectionTitle")}</Typography>
                        {!settingsLoaded && !settingsError && <CircularProgress size={14} />}
                    </Stack>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: -0.5 }}>
                        {t("auth.company.introHelperText")}
                    </Typography>
                    {settingsError && (
                        <Typography variant="body2" color="error">
                            {t("auth.company.settingsLoadFailed")}
                        </Typography>
                    )}
                    <TextField
                        fullWidth multiline minRows={3} maxRows={8}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        disabled={jerryDisabled}
                        placeholder={t("auth.company.introPlaceholder")}
                        helperText=" "
                    />

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t("auth.company.publicListingTitle")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: -0.5 }}>
                        {t("auth.company.publicListingHelper")}
                    </Typography>
                    <TextField
                        label={t("auth.company.websiteLabel")}
                        placeholder="https://yritys.fi"
                        fullWidth
                        value={website}
                        onChange={(e) => { setWebsite(e.target.value); setWebsiteError(null); }}
                        disabled={jerryDisabled}
                        error={!!websiteError}
                        helperText={websiteError || " "}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={publicConsent}
                                onChange={(e) => setPublicConsent(e.target.checked)}
                                disabled={jerryDisabled}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                {t("auth.company.publicListingConsent")}
                            </Typography>
                        }
                    />
                    <Button component="label" variant="outlined" size="small"
                        disabled={jerryDisabled || logoBusy} sx={{ alignSelf: "flex-start" }}>
                        {logoBusy ? t("common.loading") : logoDone ? t("auth.company.logoUploaded") : t("auth.company.uploadLogo")}
                        <input
                            type="file"
                            hidden
                            accept="image/png,image/jpeg,image/webp"
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                if (!file) return;
                                setLogoBusy(true);
                                setLogoError(null);
                                try {
                                    await uploadAsiakasLogo({
                                        asiakasId,
                                        file,
                                        ownerAsiakasId: asiakasId,
                                        entryByPersonId: user?.person?.[0]?.personId,
                                    });
                                    setLogoDone(true);
                                } catch (err) {
                                    captureError(err, { tags: { entity: "attachment", operation: "asiakas-logo-upload" } });
                                    setLogoError(t("auth.company.logoUploadFailed"));
                                } finally {
                                    setLogoBusy(false);
                                }
                            }}
                        />
                    </Button>
                    {logoError && <Typography variant="caption" color="error">{logoError}</Typography>}
                    {publicConsent && publicSlug && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {tRich("auth.company.publicUrlCaption", {
                                link: (
                                    <Link
                                        href={`https://betonijerry.fi/palveluntarjoajat/${publicSlug}`}
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        betonijerry.fi/palveluntarjoajat/{publicSlug}
                                    </Link>
                                ),
                            })}
                        </Typography>
                    )}

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("auth.company.jerrySettingsTitle")}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: -0.5 }}>
                        {t("auth.company.jerrySettingsHelper")}
                    </Typography>
                    {selectReady ? (
                        <TextField
                            select fullWidth label={t("auth.company.contactPersonLabel")}
                            value={jerryPersonId ?? 0}
                            onChange={(e) => { const val = Number(e.target.value); setJerryPersonId(val || null); }}
                            disabled={jerryDisabled}
                            helperText={emailHelper}
                            slotProps={{ formHelperText: emailMissing ? { sx: { color: "warning.main" } } : undefined }}
                        >
                            <MenuItem value={0}>{t("auth.company.noneSelected")}</MenuItem>
                            {currentMissing && (
                                <MenuItem value={jerryPersonId}>
                                    {jerryPersonName || t("auth.company.personFallback", { id: jerryPersonId })}
                                    <PersonEmailSpan email={jerryPersonEmail} />
                                </MenuItem>
                            )}
                            {persons.map((p) => (
                                <MenuItem key={p.personId} value={p.personId}>
                                    {[p.personFirstName, p.personLastName].filter(Boolean).join(" ")}
                                    <PersonEmailSpan email={p.personEmail} warnMissing />
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : (
                        <TextField
                            fullWidth label={t("auth.company.contactPersonLabel")}
                            value={
                                jerryPersonName
                                    ? [jerryPersonName, jerryPersonEmail].filter(Boolean).join(" — ")
                                    : t("auth.company.noneSelected")
                            }
                            disabled helperText=" "
                        />
                    )}

                    <TextField
                        fullWidth
                        label={t("auth.company.offerEmailLabel")}
                        value={offerEmail}
                        onChange={(e) => { setOfferEmail(e.target.value); setOfferEmailError(null); }}
                        disabled={jerryDisabled}
                        error={!!offerEmailError}
                        helperText={offerEmailError || t("auth.company.offerEmailHelper")}
                        slotProps={{ htmlInput: { type: "email", maxLength: 255 } }}
                    />

                    {submitError && (
                        <Typography variant="caption" color="error" sx={{ display: "block" }}>
                            {submitError}
                        </Typography>
                    )}

                    <Stack direction="row" spacing={1} sx={{ pt: 1, justifyContent: "flex-end" }}>
                        <Button onClick={onClose} disabled={status === "submitting"}>
                            {t("auth.shared.cancel")}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={status === "submitting"}
                            startIcon={status === "submitting" ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {status === "submitting" ? t("auth.shared.saving") : t("auth.shared.save")}
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </GlassDialog>
    );
}

// Dimmed email after the name in the contact-person options (and in the closed
// select, which renders the selected MenuItem's children). A missing email is
// flagged in warning color because offer emails then skip this person entirely.
function PersonEmailSpan({ email, warnMissing = false }) {
    const t = useT();
    if (!email && !warnMissing) return null;
    return (
        <Typography
            component="span"
            variant="body2"
            sx={{ ml: 1, color: email ? "text.secondary" : "warning.main" }}
        >
            {email || t("auth.company.noEmailAddress")}
        </Typography>
    );
}

function buildInitial(asiakas) {
    return {
        asiakasNimi: asiakas?.asiakasNimi ?? "",
        ytunnus: asiakas?.ytunnus ?? "",
        laskutusOsoite: asiakas?.laskutusOsoite ?? "",
        laskutusPostinumero: asiakas?.laskutusPostinumero ?? "",
        laskutusKaupunki: asiakas?.laskutusKaupunki ?? "",
    };
}
